import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
 import { motion } from 'framer-motion';
import { 
  BookOpen, Brain, Code, Globe, Lightbulb, Users, TrendingUp, Clock, 
  Star, MessageCircle, Play, Bookmark, Target, Calendar, Award, 
  Coffee, Sun, Moon, Settings, Bell, Search, ChevronRight, LogOut,
  Zap, Heart, Eye, ThumbsUp, Share2, Filter, ArrowRight,BadgeCheck, Send,
  Headphones, FileText, Video, PenTool, Languages, Calculator,
  BarChart2, PieChart, LineChart, Book, Newspaper, Database, Plus, Home,
  Feather, Terminal, Cpu, Shield, GitBranch, Smartphone, RefreshCw,
  Camera, Music, Mic, Film, Image, Download, Upload, ChevronLeft,
  ChevronDown, ChevronUp, X, Maximize2, Minimize2, RotateCw,
  Compass
} from 'lucide-react';
import Loading from './Loading';
import Avatar from './Avatar';
import ProfilePopup from './ProfilePopup';
import ELibrary from './ELibrary';
import LearningNews from './LearningNews';
import LearningAnalytics from './LearningAnalytics';
import DiscoveryLearning from './Discovery';
import CommunityLearning from './CommunityLearning';
import EventLearning from './EventLearning';
import SettingsHub from './Settings';

const LearningPlatformHome = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [user, setUser] = useState(null);
  const [darkMode, setDarkMode] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedBook, setExpandedBook] = useState(null);
  const [showNewsDetail, setShowNewsDetail] = useState(null);
  const [readingTime, setReadingTime] = useState(0);
  const [isReading, setIsReading] = useState(false);
  const readingTimerRef = useRef(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showLoading, setShowLoading] = useState(true);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const navigate = useNavigate();



  // Learning data with enhanced metrics
  const learningStats = {
    streak: 7,
    hoursThisWeek: 12.5,
    completedCourses: 8,
    currentGoal: 'Learn React Hooks',
    bestTime: '9:00 AM - 11:00 AM',
    focusScore: 87,
    weeklyProgress: [4.2, 5.1, 3.8, 6.2, 5.5, 7.1, 4.9],
    knowledgeGraph: {
      frontend: 78,
      backend: 65,
      databases: 58,
      devops: 42,
      design: 35
    }
  };

  // Featured content with real course data
  const featuredContent = [
    {
      id: 1,
      title: 'Advanced AI & Machine Learning',
      subtitle: 'Master neural networks and deep learning',
      category: 'AI',
      image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1080&q=80',
      color: 'from-blue-600 to-cyan-600',
      duration: '8 weeks',
      rating: 4.9,
      students: '12.5K',
      instructor: 'Dr. Sarah Chen',
      level: 'Advanced',
      syllabus: ['Neural Networks', 'TensorFlow', 'Computer Vision', 'NLP']
    },
    {
      id: 2,
      title: 'Full-Stack Development Bootcamp',
      subtitle: 'From frontend to backend mastery',
      category: 'Programming',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1080&q=80',
      color: 'from-green-600 to-emerald-600',
      duration: '12 weeks',
      rating: 4.8,
      students: '8.2K',
      instructor: 'James Rodriguez',
      level: 'Intermediate',
      syllabus: ['React', 'Node.js', 'Express', 'MongoDB']
    },
    {
      id: 3,
      title: 'Cloud Architecture & DevOps',
      subtitle: 'AWS, Docker, Kubernetes mastery',
      category: 'Networking',
      image: 'https://images.unsplash.com/photo-1624953587687-daf255b6b80a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1080&q=80',
      color: 'from-purple-600 to-violet-600',
      duration: '10 weeks',
      rating: 4.7,
      students: '6.1K',
      instructor: 'Emma Thompson',
      level: 'Advanced',
      syllabus: ['AWS Services', 'CI/CD Pipelines', 'Infrastructure as Code']
    },
    {
      id: 4,
      title: 'UX/UI Design Fundamentals',
      subtitle: 'Create beautiful, user-centered designs',
      category: 'Design',
      image: 'https://images.unsplash.com/photo-1541462608143-67571c6738dd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1080&q=80',
      color: 'from-pink-600 to-rose-600',
      duration: '6 weeks',
      rating: 4.9,
      students: '15.3K',
      instructor: 'Michael Chen',
      level: 'Beginner',
      syllabus: ['User Research', 'Wireframing', 'Prototyping', 'Figma']
    }
  ];

  // Learning news with real-world examples
  const learningNews = [
    {
      id: 1,
      title: 'OpenAI Releases GPT-4o with Multimodal Capabilities',
      summary: 'The new model can process text, audio, and images simultaneously, enabling more natural human-computer interactions.',
      category: 'AI',
      date: '2023-05-15',
      source: 'TechCrunch',
      readTime: '4 min',
      image: 'https://images.unsplash.com/photo-1644440901493-30c9f0d67017?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1080&q=80',
      trending: true,
      content: `
        <p>OpenAI has unveiled GPT-4o, its newest flagship AI model that can reason across audio, vision, and text in real time. This represents a significant leap in AI capabilities, with the model responding to audio inputs in as little as 232 milliseconds, similar to human response time in conversation.</p>
        <p>Key features include:</p>
        <ul>
          <li>Improved multilingual performance across 50+ languages</li>
          <li>Enhanced vision capabilities for complex image analysis</li>
          <li>More natural voice interactions with emotional range</li>
          <li>50% reduction in API costs compared to GPT-4 Turbo</li>
        </ul>
        <p>The model is being rolled out to ChatGPT Plus users this week, with enterprise availability coming next month.</p>
      `
    },
    {
      id: 2,
      title: 'Rust 1.75 Released with Async Improvements',
      summary: 'The latest version of the systems programming language introduces better async trait support and other developer experience enhancements.',
      category: 'Programming',
      date: '2023-12-01',
      source: 'Rust Blog',
      readTime: '6 min',
      image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1080&q=80',
      trending: false,
      content: `
        <p>The Rust team has announced Rust 1.75, bringing several quality-of-life improvements for developers working with asynchronous code. The headline feature is the stabilization of async trait methods, which has been one of the most requested additions to the language.</p>
        <p>Key updates include:</p>
        <ul>
          <li>Async trait methods now stable after years in development</li>
          <li>Improved error messages for trait bounds</li>
          <li>New #[diagnostic] attribute for custom warnings</li>
          <li>Performance improvements to the borrow checker</li>
        </ul>
        <p>This release continues Rust's trajectory as one of the fastest-growing programming languages, particularly in systems programming and performance-critical applications.</p>
      `
    },
    {
      id: 3,
      title: 'WebAssembly 2.0 Draft Specification Published',
      summary: 'The next generation of WebAssembly promises better performance, threading support, and garbage collection capabilities.',
      category: 'Web Development',
      date: '2023-11-20',
      source: 'W3C',
      readTime: '5 min',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1080&q=80',
      trending: true,
      content: `
        <p>The WebAssembly Working Group has published the first public working draft of WebAssembly 2.0, setting the stage for major performance and capability improvements in web applications.</p>
        <p>Notable new features include:</p>
        <ul>
          <li>Threading support for true parallel execution</li>
          <li>Garbage collection for easier integration with managed languages</li>
          <li>SIMD instructions for vector operations</li>
          <li>Improved debugging capabilities</li>
        </ul>
        <p>Browser vendors expect to begin implementation of these features in early 2024, with full standardization anticipated by mid-year.</p>
      `
    },
    {
      id: 4,
      title: 'React 19 Beta Released with Compiler Optimizations',
      summary: 'The upcoming React version introduces an optimizing compiler that reduces the need for manual memoization.',
      category: 'Frontend',
      date: '2023-10-15',
      source: 'React Blog',
      readTime: '7 min',
      image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1080&q=80',
      trending: true,
      content: `
        <p>The React team has unveiled the beta release of React 19, featuring a groundbreaking compiler that automatically optimizes component re-renders. This represents a fundamental shift in how React applications achieve performance.</p>
        <p>Major improvements include:</p>
        <ul>
          <li>React Compiler that eliminates unnecessary re-renders</li>
          <li>Simplified state management with Actions</li>
          <li>Built-in support for Web Components</li>
          <li>Improved hydration for server-rendered apps</li>
        </ul>
        <p>Early benchmarks show up to 30% performance improvements in complex applications without any code changes. The stable release is expected in Q1 2024.</p>
      `
    }
  ];

  // Enhanced quick actions
  const quickActions = [
    { 
      icon: BookOpen, 
      label: 'Continue Reading', 
      action: 'The Clean Coder - Chapter 8', 
      color: 'bg-blue-500',
      type: 'reading'
    },
    { 
      icon: Play, 
      label: 'Watch Next', 
      action: 'JavaScript Promises Tutorial', 
      color: 'bg-red-500',
      type: 'video'
    },
    { 
      icon: PenTool, 
      label: 'Practice Flashcards', 
      action: '23 React concepts to review', 
      color: 'bg-green-500',
      type: 'practice'
    },
    { 
      icon: Lightbulb, 
      label: 'Add Idea', 
      action: 'Capture your learning insights', 
      color: 'bg-yellow-500',
      type: 'note'
    },
    { 
      icon: Users, 
      label: 'Study Group', 
      action: 'Join "Advanced JS" discussion', 
      color: 'bg-purple-500',
      type: 'community'
    },
    { 
      icon: Languages, 
      label: 'Language Practice', 
      action: 'Spanish - 15 new words', 
      color: 'bg-orange-500',
      type: 'language'
    }
  ];

  // Community highlights with real user data
  const communityHighlights = [
    {
      user: 'Sarah Chen',
      avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      action: 'shared a breakthrough',
      content: 'Finally understood async/await patterns after building this promise-based queue system!',
      topic: 'JavaScript',
      likes: 24,
      replies: 8,
      timeAgo: '2h',
      badges: ['expert', 'mentor']
    },
    {
      user: 'Marcus Rodriguez',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      action: 'completed',
      content: 'Machine Learning Fundamentals Course with 98% score on final project',
      topic: 'AI/ML',
      likes: 18,
      replies: 12,
      timeAgo: '4h',
      badges: ['achiever']
    },
    {
      user: 'Emma Thompson',
      avatar: 'https://randomuser.me/api/portraits/women/63.jpg',
      action: 'asked a question',
      content: 'Best practices for microservices architecture in large-scale e-commerce applications?',
      topic: 'Backend',
      likes: 31,
      replies: 15,
      timeAgo: '6h',
      badges: ['architect']
    }
  ];

  // Today's suggestions with more detail
  const todaysSuggestions = [
    {
      type: 'optimal-time',
      icon: Clock,
      title: 'Perfect Study Time',
      description: 'Your peak focus is in 2 hours (9:00 AM) based on historical data',
      action: 'Set Reminder',
      color: 'bg-blue-500'
    },
    {
      type: 'streak',
      icon: Target,
      title: 'Maintain Your Streak',
      description: 'Read for 30 minutes to keep your 7-day learning streak',
      action: 'Start Reading',
      color: 'bg-orange-500'
    },
    {
      type: 'community',
      icon: Users,
      title: 'Join Discussion',
      description: '5 new replies in "React Best Practices" thread you follow',
      action: 'View Thread',
      color: 'bg-green-500'
    },
    {
      type: 'practice',
      icon: Brain,
      title: 'Quick Practice',
      description: 'Review 10 flashcards while having your morning coffee',
      action: 'Start Quiz',
      color: 'bg-purple-500'
    }
  ];

  // Enhanced reading recommendations with book covers
  const readingRecommendations = [
    { 
      title: 'Clean Architecture', 
      author: 'Robert C. Martin', 
      progress: 65, 
      rating: 4.8,
      cover: 'https://images-na.ssl-images-amazon.com/images/I/41vS7Qp4UZL._SX218_BO1,204,203,200_QL40_FMwebp_.jpg',
      summary: 'A practical guide to software architecture that teaches how to structure systems to be maintainable, testable, and flexible.',
      pages: 432,
      currentChapter: 'Chapter 9: Business Rules',
      lastRead: '2 days ago',
      quotes: [
        "The only way to go fast, is to go well.",
        "Architecture is about the important stuff. Whatever that is."
      ]
    },
    { 
      title: 'System Design Interview', 
      author: 'Alex Xu', 
      progress: 0, 
      rating: 4.9,
      cover: 'https://images-na.ssl-images-amazon.com/images/I/51H3hXJ0VCL._SX218_BO1,204,203,200_QL40_FMwebp_.jpg',
      summary: 'An insider\'s guide to system design interviews with real-world examples and scalable system patterns.',
      pages: 320,
      currentChapter: 'Not started',
      lastRead: '',
      quotes: []
    },
    { 
      title: 'The Pragmatic Programmer', 
      author: 'David Thomas, Andrew Hunt', 
      progress: 100, 
      rating: 4.7,
      cover: 'https://images-na.ssl-images-amazon.com/images/I/51W1sBPO7tL._SX218_BO1,204,203,200_QL40_FMwebp_.jpg',
      summary: 'Your journey to mastery with timeless advice for career success in software development.',
      pages: 352,
      currentChapter: 'Completed',
      lastRead: '3 weeks ago',
      quotes: [
        "Think! About Your Work",
        "It's both what you say and the way you say it that matters."
      ]
    }
  ];

  // E-library with different resource types
  const eLibrary = [
    {
      type: 'book',
      title: 'Designing Data-Intensive Applications',
      author: 'Martin Kleppmann',
      category: 'Databases',
      progress: 42,
      cover: 'https://images-na.ssl-images-amazon.com/images/I/51zTz8D0+FL._SX218_BO1,204,203,200_QL40_FMwebp_.jpg'
    },
    {
      type: 'video-course',
      title: 'Advanced CSS and Sass',
      author: 'Jonas Schmedtmann',
      category: 'Frontend',
      progress: 78,
      cover: 'https://img-a.udemycdn.com/course/240x135/1026604_790b_2.jpg'
    },
    {
      type: 'research-paper',
      title: 'The Calculus of Computation',
      author: 'Aaron R. Bradley',
      category: 'Computer Science',
      progress: 15,
      cover: 'https://images.springer.com/sgw/books/medium/9780387763663.jpg'
    },
    {
      type: 'audiobook',
      title: 'Atomic Habits',
      author: 'James Clear',
      category: 'Productivity',
      progress: 100,
      cover: 'https://images-na.ssl-images-amazon.com/images/I/51B7kuFwQFL._SY291_BO1,204,203,200_QL40_FMwebp_.jpg'
    }
  ];

  // Knowledge graph data for visualization
  const knowledgeGraphOptions = {
    chart: {
      height: 350,
      type: 'radar',
      toolbar: {
        show: false
      }
    },
    colors: ['#3B82F6'],
    xaxis: {
      categories: ['Frontend', 'Backend', 'Databases', 'DevOps', 'Design']
    },
    yaxis: {
      show: false,
      min: 0,
      max: 100
    },
    markers: {
      size: 5,
      hover: {
        size: 7
      }
    },
    tooltip: {
      y: {
        formatter: function(val) {
          return val + '% mastery';
        }
      }
    },
    fill: {
      opacity: 0.1
    },
    stroke: {
      width: 2
    }
  };

  const knowledgeGraphSeries = [{
    name: 'Knowledge Level',
    data: Object.values(learningStats.knowledgeGraph)
  }];

  // Weekly progress chart
  const weeklyProgressOptions = {
    chart: {
      height: 300,
      type: 'area',
      toolbar: {
        show: false
      }
    },
    colors: ['#10B981'],
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 2
    },
    xaxis: {
      categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      labels: {
        style: {
          colors: darkMode ? '#E5E7EB' : '#6B7280'
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: darkMode ? '#E5E7EB' : '#6B7280'
        },
        formatter: function(val) {
          return val + 'h';
        }
      }
    },
    tooltip: {
      y: {
        formatter: function(val) {
          return val + ' hours';
        }
      }
    },
    grid: {
      borderColor: darkMode ? '#374151' : '#E5E7EB'
    }
  };

  const weeklyProgressSeries = [{
    name: 'Learning Hours',
    data: learningStats.weeklyProgress
  }];


  // Add these state variables to your component

const [carouselTilt, setCarouselTilt] = useState(0);

const [showAllQuotes, setShowAllQuotes] = useState(false);
const [progress, setProgress] = useState(0);

// Add these function definitions to your component
const handleSuggestionClick = (suggestion) => {
  console.log('Selected suggestion:', suggestion);
  // Implement your suggestion click logic here
};

const handleCarouselNav = (direction) => {
  if (direction === 'prev') {
    setCurrentSlide(prev => (prev === 0 ? featuredContent.length - 1 : prev - 1));
  } else {
    setCurrentSlide(prev => (prev === featuredContent.length - 1 ? 0 : prev + 1));
  }
  setProgress(0); // Reset progress on navigation
};

const handleCarouselTilt = (e) => {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const centerX = rect.width / 2;
  const tilt = (x - centerX) / 20; // Adjust divisor for more/less tilt
  setCarouselTilt(tilt);
};

const openPostModal = (post) => {
  console.log('Opening post modal:', post);
  // Implement your modal opening logic here
};


  // Start/stop reading timer
  const toggleReadingTimer = () => {
    if (isReading) {
      clearInterval(readingTimerRef.current);
      setIsReading(false);
    } else {
      setIsReading(true);
      readingTimerRef.current = setInterval(() => {
        setReadingTime(prev => prev + 1);
      }, 1000);
    }
  };

  // Format time for reading timer
  const formatReadingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

 
  useEffect(() => {
    const slideTimer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredContent.length);
    }, 5000);
    return () => clearInterval(slideTimer);
  }, []);

  useEffect(() => {
    return () => {
      if (readingTimerRef.current) {
        clearInterval(readingTimerRef.current);
      }
    };
  }, []);


  useEffect(() => {
    // Fetch user data on component mount
    const fetchUserData = async () => {
      try {
        const response = await fetch('/api/pc/auth/me', {
          method: 'GET',
          credentials: 'include' 
        });

        if (!response.ok) {
          throw new Error('Unauthorized');
        }

        const userData = await response.json();
        //console.log("userData", userData)
        setUser(userData);
        //setIsLoading(false);
      } catch (error) {
        //console.error('Failed to fetch user:', error);
        // Redirect to login if not authenticated
        navigate('/PeaceAuth');
      }
    };

    fetchUserData();

    // Time and other effects
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, [navigate]);



    useEffect(() => {
    // Simulate loading time (e.g., 5 seconds)
    const timer = setTimeout(() => setShowLoading(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  if (showLoading) {
    return <Loading user={user} />; 
  }

  const getTimeBasedGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getMotivationalMessage = () => {
    const messages = [
      "Ready to level up your skills?",
      "Your learning journey continues today",
      "Every expert was once a beginner",
      "Knowledge is the ultimate superpower",
      "What will you learn today?",
      "Small steps lead to big achievements"
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const toggleBookExpand = (bookIndex) => {
    if (expandedBook === bookIndex) {
      setExpandedBook(null);
    } else {
      setExpandedBook(bookIndex);
    }
  };

  const toggleNewsDetail = (newsId) => {
    setShowNewsDetail(showNewsDetail === newsId ? null : newsId);
  };

  const handleLogout = async () => {
    try {
      const response = await fetch('/api/pc/auth/logout', {
        method: 'POST',
        credentials: 'include' // Important for HTTP-only cookies
      });

      if (response.ok) {
        // Redirect to login page
        window.location.href = '/PeaceCraft';
      } else {
        const errorData = await response.json();       
        alert('Logout failed. Please try again.');
      }
    } catch (error) {     
      alert('Network error during logout');
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      {/* Sidebar */}
      <motion.div 
        initial={{ width: sidebarOpen ? 280 : 80 }}
        animate={{ width: sidebarOpen ? 280 : 80 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`fixed top-0 left-0 h-full flex flex-col z-20 ${darkMode ? 'bg-gray-900' : 'bg-white'} shadow-2xl overflow-hidden`}
      >
        {/* Header with smooth toggle */}
        <div className={`flex items-center ${sidebarOpen ? 'justify-between px-6' : 'justify-center'} py-5 border-b ${darkMode ? 'border-gray-800' : 'border-gray-100'}`}>
          {sidebarOpen ? (
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center"
            >
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-2">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Peacecraft
              </h1>
            </motion.div>
          ) : (
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
          )}
          
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className={`p-1.5 rounded-lg ${darkMode ? 'hover:bg-gray-800 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-black'}`}
          >
            {sidebarOpen ? (
              <ChevronLeft className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation with hover effects */}
        <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          {[
            { icon: Home, label: "Dashboard", tab: "dashboard" },
            { icon: BookOpen, label: "E-Library", tab: "library" },
            { icon: Newspaper, label: "Learning News", tab: "news" },
            { icon: BarChart2, label: "Analytics", tab: "analytics" },
            { icon: Compass, label: "Discover", tab: "discover" },
            { icon: Users, label: "Community", tab: "community" },
            { icon: Calendar, label: "Events", tab: "events" },
            { icon: Settings, label: "Settings", tab: "settings" },
          ].map((item, index) => (
            <motion.div
              key={item.tab}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`flex items-center ${sidebarOpen ? 'px-6' : 'justify-center'} py-3 mx-2 rounded-lg ${
                activeTab === item.tab
                  ? darkMode
                    ? 'bg-gradient-to-r from-blue-900/50 to-purple-900/50 border-l-4 border-blue-500'
                    : 'bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-400'
                  : ''
              } cursor-pointer transition-colors ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
              onClick={() => setActiveTab(item.tab)}
            >
              <div className={`p-1.5 rounded-lg ${
                activeTab === item.tab
                  ? darkMode
                    ? 'bg-blue-600/20 text-blue-400'
                    : 'bg-blue-500/20 text-blue-500'
                  : darkMode
                    ? 'bg-gray-800 text-gray-400'
                    : 'bg-gray-100 text-gray-500'
              }`}>
                <item.icon className="w-5 h-5" />
              </div>
              {sidebarOpen && (
                <motion.span 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="ml-3 font-medium"
                >
                  {item.label}
                </motion.span>
              )}
            </motion.div>
          ))}
        </nav>

        {/* User Profile Section */}
        <div className={`border-t ${darkMode ? 'border-gray-800' : 'border-gray-100'} p-4`}>
          <motion.div 
            whileHover={{ scale: 1.01 }}
            className={`flex items-center p-2 rounded-lg cursor-pointer ${
              darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'
            }`}
            onClick={() => setShowProfilePopup(true)}
          >
            <div className="relative">
              <Avatar
                firstName={user?.first_name || ''}
                lastName={user?.last_name || ''}
                className="w-10 h-10 text-sm"
              />
              <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${
                darkMode ? 'border-gray-900' : 'border-white'
              } ${user?.status === 'online' ? 'bg-green-500' : 'bg-gray-500'}`}></div>
            </div>
            
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="ml-3 overflow-hidden"
              >
                <div className="font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </div>
                <div className={`text-xs flex items-center ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <span className="truncate">{user?.email}</span>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Profile Popup */}
        {showProfilePopup && (
          <ProfilePopup 
            user={{ ...user, darkMode }} 
            onClose={() => setShowProfilePopup(false)}
            onLogout={handleLogout}
          />
        )}
      </motion.div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        <div className="p-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className={`text-4xl font-light mb-2`}>
                {getTimeBasedGreeting()}, <span className="font-medium bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">{user.first_name}</span>
              </h1>
              <p className={`text-lg font-light ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {getMotivationalMessage()}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button className={`p-3 rounded-full backdrop-blur-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-100'} shadow-md transition-all duration-300`}>
                <Bell className="w-5 h-5" />
              </button>
              <button className={`p-3 rounded-full backdrop-blur-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-100'} shadow-md transition-all duration-300`}>
                <Search className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setDarkMode(!darkMode)}
                className={`p-3 rounded-full backdrop-blur-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-100'} shadow-md transition-all duration-300`}
              >
                {darkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-indigo-600" />}
              </button>
               {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className={`p-3 rounded-full backdrop-blur-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-100'} shadow-md transition-all duration-300`}
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
            </div>
          </div>

          {/* Dashboard Content */}
          {activeTab === 'dashboard' && (
            <>
              {/* Animated Header with User Greeting */}
              <div className="mb-8">
                <div className={`p-6 rounded-3xl ${darkMode ? 'bg-gradient-to-r from-gray-800 to-gray-700' : 'bg-gradient-to-r from-blue-50 to-indigo-50'} shadow-lg flex justify-between items-center`}>
                  <div>
                    <h1 className="text-3xl font-light mb-1">Welcome back, <span className="font-medium">{user.first_name}</span> 👋</h1>
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Here's what's happening with your learning today</p>
                  </div>
                  <div className="relative">
                    <Avatar
                      firstName={user?.first_name || ''}
                      lastName={user?.last_name || ''}
                      className="w-16 h-16 rounded-full border-4 border-white/20 shadow-lg"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-green-400 rounded-full w-5 h-5 border-2 border-white"></div>
                  </div>
                </div>
              </div>

              {/* Enhanced Learning Stats with Progress Bars */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className={`p-5 rounded-3xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl hover:shadow-2xl transition-shadow duration-300 group`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-orange-500/10 rounded-xl">
                        <Target className="w-6 h-6 text-orange-400" />
                      </div>
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Current Streak</p>
                        <div className="text-2xl font-semibold flex items-end">
                          {learningStats.streak}
                          <span className="text-sm font-normal ml-1">days</span>
                        </div>
                      </div>
                    </div>
                    <div className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                      🔥 Active
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span>Weekly goal: 7 days</span>
                      <span>{Math.min(learningStats.streak, 7)}/7</span>
                    </div>
                    <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className="h-2 rounded-full bg-gradient-to-r from-orange-400 to-yellow-400" 
                        style={{ width: `${Math.min(learningStats.streak/7*100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className={`p-5 rounded-3xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl hover:shadow-2xl transition-shadow duration-300 group`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-green-500/10 rounded-xl">
                        <Clock className="w-6 h-6 text-green-400" />
                      </div>
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Learning Time</p>
                        <div className="text-2xl font-semibold flex items-end">
                          {learningStats.hoursThisWeek}
                          <span className="text-sm font-normal ml-1">hours</span>
                        </div>
                      </div>
                    </div>
                    <div className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700'}`}>
                      {learningStats.hoursThisWeek > 10 ? '🚀 Productive' : 'Keep going'}
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span>Weekly goal: 15h</span>
                      <span>{learningStats.hoursThisWeek}/15</span>
                    </div>
                    <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className="h-2 rounded-full bg-gradient-to-r from-green-400 to-teal-400" 
                        style={{ width: `${Math.min(learningStats.hoursThisWeek/15*100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className={`p-5 rounded-3xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl hover:shadow-2xl transition-shadow duration-300 group`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-blue-500/10 rounded-xl">
                        <Award className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Courses Completed</p>
                        <div className="text-2xl font-semibold flex items-end">
                          {learningStats.completedCourses}
                          <span className="text-sm font-normal ml-1">courses</span>
                        </div>
                      </div>
                    </div>
                    <div className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                      {learningStats.completedCourses > 5 ? '🏆 Achiever' : 'On track'}
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span>Monthly goal: 3 courses</span>
                      <span>{Math.min(learningStats.completedCourses, 3)}/3</span>
                    </div>
                    <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className="h-2 rounded-full bg-gradient-to-r from-blue-400 to-indigo-400" 
                        style={{ width: `${Math.min(learningStats.completedCourses/3*100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className={`p-5 rounded-3xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl hover:shadow-2xl transition-shadow duration-300 group`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-purple-500/10 rounded-xl">
                        <Brain className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Focus Score</p>
                        <div className="text-2xl font-semibold flex items-end">
                          {learningStats.focusScore}
                          <span className="text-sm font-normal ml-1">%</span>
                        </div>
                      </div>
                    </div>
                    <div className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                      {learningStats.focusScore > 80 ? '🧠 Focused' : 'Can improve'}
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span>Daily average</span>
                      <span>vs {learningStats.focusScore + 5}% yesterday</span>
                    </div>
                    <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className="h-2 rounded-full bg-gradient-to-r from-purple-400 to-pink-400" 
                        style={{ width: `${learningStats.focusScore}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personalized Recommendations with Interactive Cards */}
              <div className={`p-6 rounded-3xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-2xl mb-8`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <Zap className="w-6 h-6 text-yellow-400 animate-pulse" />
                    <h2 className="text-2xl font-light">Personalized Recommendations</h2>
                  </div>
                  <div className="flex space-x-2">
                    <button className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button className={`text-sm font-medium text-blue-500 hover:text-blue-400`}>
                      View All →
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {todaysSuggestions.map((suggestion, index) => (
                    <div 
                      key={index} 
                      className={`p-5 rounded-2xl ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-blue-50 hover:bg-blue-100'} transition-all duration-300 cursor-pointer group relative overflow-hidden`}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {/* Background pattern/illustration */}
                      <div className={`absolute -right-5 -top-5 w-20 h-20 rounded-full ${suggestion.color.replace('bg-', 'bg-').replace('text-', 'text-')} opacity-10 group-hover:opacity-20 transition-opacity`}></div>
                      
                      <div className="flex items-start space-x-4 relative z-10">
                        <div className={`p-3 rounded-xl ${suggestion.color} shadow-md`}>
                          <suggestion.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-medium mb-1`}>{suggestion.title}</h4>
                          <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{suggestion.description}</p>
                          <div className="flex justify-between items-center">
                            <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-white text-gray-600'}`}>
                              {suggestion.difficulty}
                            </span>
                            <button className={`text-sm font-medium flex items-center text-blue-500 hover:text-blue-400`}>
                              {suggestion.action} <ArrowRight className="ml-1 w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Interactive Featured Content Carousel with 3D Effect */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-light">Featured Learning Paths</h2>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => handleCarouselNav('prev')}
                      className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => handleCarouselNav('next')}
                      className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                <div className="relative h-[400px] rounded-3xl overflow-hidden">
                  {/* Background with parallax effect */}
                  <div className="absolute inset-0 overflow-hidden">
                    <img 
                      src={featuredContent[currentSlide].background}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover transform scale-110"
                      style={{ filter: 'blur(20px)', opacity: 0.3 }}
                    />
                  </div>
                  
                  {/* Main content with 3D perspective */}
                  <div className="relative h-full flex items-center justify-center perspective-1000">
                    <div 
                      className="relative w-full max-w-5xl h-80 mx-auto transition-transform duration-500"
                      style={{ transform: `rotateY(${carouselTilt}deg)` }}
                      onMouseMove={handleCarouselTilt}
                      onMouseLeave={() => setCarouselTilt(0)}
                    >
                      <div className={`absolute inset-0 rounded-3xl overflow-hidden shadow-2xl transform transition-transform duration-300 hover:scale-[1.02]`}>
                        <div className={`absolute inset-0 bg-gradient-to-r ${featuredContent[currentSlide].color}`}>
                          <img 
                            src={featuredContent[currentSlide].image} 
                            alt={featuredContent[currentSlide].title}
                            className="absolute inset-0 w-full h-full object-cover opacity-80"
                          />
                          
                          <div className="relative h-full flex items-center p-10">
                            <div className="text-white flex-1 max-w-xl">
                              <div className="mb-3">
                                <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-md">
                                  {featuredContent[currentSlide].category}
                                </span>
                              </div>
                              <h3 className="text-4xl font-light mb-3 leading-tight">{featuredContent[currentSlide].title}</h3>
                              <p className="text-xl opacity-90 font-light mb-6">{featuredContent[currentSlide].subtitle}</p>
                              
                              <div className="flex flex-wrap gap-3 mb-6 text-sm">
                                <div className="flex items-center space-x-1 bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm">
                                  <Clock className="w-4 h-4" />
                                  <span>{featuredContent[currentSlide].duration}</span>
                                </div>
                                <div className="flex items-center space-x-1 bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm">
                                  <Star className="w-4 h-4 text-yellow-300 fill-current" />
                                  <span>{featuredContent[currentSlide].rating}</span>
                                </div>
                                <div className="flex items-center space-x-1 bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm">
                                  <Users className="w-4 h-4" />
                                  <span>{featuredContent[currentSlide].students} students</span>
                                </div>
                                {featuredContent[currentSlide].certified && (
                                  <div className="flex items-center space-x-1 bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm">
                                    <BadgeCheck className="w-4 h-4 text-blue-300" />
                                    <span>Certified</span>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex space-x-4">
                                <button className="px-8 py-3 bg-white text-gray-800 font-medium rounded-full hover:bg-gray-100 transition-all duration-300 flex items-center">
                                  Start Learning <ArrowRight className="ml-2 w-4 h-4" />
                                </button>
                                <button className="px-6 py-3 bg-white/20 backdrop-blur-md text-white font-medium rounded-full hover:bg-white/30 transition-all duration-300 flex items-center">
                                  <Bookmark className="w-4 h-4 mr-2" /> Save
                                </button>
                              </div>
                            </div>
                            
                            {/* Course preview video thumbnail */}
                            <div className="hidden lg:block flex-1">
                              <div className="relative w-80 h-48 rounded-xl overflow-hidden shadow-2xl transform transition-transform duration-300 hover:scale-105">
                                <img 
                                  src={featuredContent[currentSlide].previewThumbnail} 
                                  alt="Course preview"
                                  className="absolute inset-0 w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                  <button className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                                    <Play className="w-5 h-5 text-gray-800 ml-1" />
                                  </button>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                                  <p className="text-white text-sm">Preview: {featuredContent[currentSlide].previewTitle}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Navigation dots with progress indicator */}
                  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center space-x-4">
                    <button 
                      onClick={() => setCurrentSlide((prev) => (prev === 0 ? featuredContent.length - 1 : prev - 1))}
                      className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <div className="flex space-x-2">
                      {featuredContent.map((_, index) => (
                        <button
                          key={index}
                          className={`w-3 h-3 rounded-full transition-all duration-300 relative overflow-hidden ${
                            index === currentSlide ? 'bg-white' : 'bg-white/50'
                          }`}
                          onClick={() => setCurrentSlide(index)}
                        >
                          {index === currentSlide && (
                            <div 
                              className="absolute top-0 left-0 h-full bg-white/50"
                              style={{ 
                                width: `${progress}%`,
                                animation: 'progressAnimation 5s linear forwards'
                              }}
                            ></div>
                          )}
                        </button>
                      ))}
                    </div>
                    
                    <button 
                      onClick={() => setCurrentSlide((prev) => (prev === featuredContent.length - 1 ? 0 : prev + 1))}
                      className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Quick Actions with Hover Effects */}
              <div className="mb-8">
                <h2 className="text-2xl font-light mb-6">Quick Actions</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      className={`p-5 rounded-2xl ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'} shadow-lg transition-all duration-300 group relative overflow-hidden`}
                    >
                      {/* Hover effect background */}
                      <div className={`absolute inset-0 ${action.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                      
                      <div className="relative z-10">
                        <div className={`w-12 h-12 ${action.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                          <action.icon className="w-6 h-6 text-white" />
                        </div>
                        <h4 className={`font-medium mb-2 text-left`}>{action.label}</h4>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} text-left`}>{action.action}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Content Grid with Enhanced Components */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Community Highlights with Interactive Posts */}
                <div className={`lg:col-span-2 p-6 rounded-3xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <Users className="w-6 h-6 text-green-400" />
                      <h3 className="text-xl font-light">Community Highlights</h3>
                    </div>
                    <div className="flex space-x-3">
                      <button className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                        <Filter className="w-4 h-4" />
                      </button>
                      <button className={`text-sm font-medium text-blue-500 hover:text-blue-400`}>
                        Join Discussion →
                      </button>
                    </div>
                  </div>
                  
                  {/* Community feed with interactive elements */}
                  <div className="space-y-4">
                    {communityHighlights.map((highlight, index) => (
                      <div 
                        key={index} 
                        className={`p-5 rounded-2xl ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} transition-all duration-300 cursor-pointer group`}
                        onClick={() => openPostModal(highlight)}
                      >
                        <div className="flex items-start space-x-4">
                          <div className="relative">
                            <img 
                              src={highlight.avatar} 
                              alt={highlight.user}
                              className="w-12 h-12 rounded-full border-2 border-white/20"
                            />
                            {highlight.isOnline && (
                              <div className="absolute bottom-0 right-0 bg-green-400 rounded-full w-3 h-3 border-2 border-white"></div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              <span className={`font-medium`}>{highlight.user}</span>
                              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{highlight.action}</span>
                              <span className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                {highlight.topic}
                              </span>
                              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{highlight.timeAgo}</span>
                            </div>
                            
                            <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{highlight.content}</p>
                            
                            {/* Post image/video preview */}
                            {highlight.media && (
                              <div className="mb-4 rounded-xl overflow-hidden">
                                <img 
                                  src={highlight.media} 
                                  alt="Post content"
                                  className="w-full h-auto max-h-60 object-cover"
                                />
                              </div>
                            )}
                            
                            {/* Interactive buttons */}
                            <div className="flex items-center space-x-4 text-sm">
                              <button className={`flex items-center space-x-1 ${highlight.liked ? 'text-red-500' : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
                                <Heart className={`w-4 h-4 ${highlight.liked ? 'fill-current' : ''}`} />
                                <span>{highlight.likes}</span>
                              </button>
                              <button className={`flex items-center space-x-1 ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
                                <MessageCircle className="w-4 h-4" />
                                <span>{highlight.replies}</span>
                              </button>
                              <button className={`flex items-center space-x-1 ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
                                <Share2 className="w-4 h-4" />
                                <span>Share</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* New post input */}
                  <div className={`mt-6 p-4 rounded-2xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex items-center space-x-3">
                      <img 
                        src="/images/user-avatar.png" 
                        alt="Your profile"
                        className="w-10 h-10 rounded-full"
                      />
                      <input 
                        type="text" 
                        placeholder="Share your thoughts with the community..."
                        className={`flex-1 bg-transparent outline-none ${darkMode ? 'placeholder-gray-500' : 'placeholder-gray-400'}`}
                      />
                      <button className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors">
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Enhanced Reading Progress with Book Visualization */}
                <div className={`p-6 rounded-3xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <BookOpen className="w-6 h-6 text-blue-400" />
                      <h3 className="text-xl font-light">Reading Progress</h3>
                    </div>
                    <button className={`text-sm font-medium text-blue-500 hover:text-blue-400`}>
                      Add Book +
                    </button>
                  </div>
                  
                  <div className="space-y-5">
                    {readingRecommendations.map((book, index) => (
                      <div 
                        key={index} 
                        className={`p-5 rounded-2xl ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} transition-all duration-300`}
                      >
                        <div className="flex items-start space-x-4">
                          {/* Book cover with progress visualization */}
                          <div className="relative">
                            <img 
                              src={book.cover} 
                              alt={book.title}
                              className="w-14 h-20 object-cover rounded shadow-md"
                            />
                            <div className="absolute -bottom-1 -left-1 right-0 h-1 rounded-full bg-gray-300 overflow-hidden">
                              <div 
                                className="h-full bg-blue-500" 
                                style={{ width: `${book.progress}%` }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className={`font-medium mb-1`}>{book.title}</h4>
                                <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{book.author}</p>
                              </div>
                              <button 
                                className={`p-1 rounded-full ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                                onClick={() => toggleBookExpand(index)}
                              >
                                {expandedBook === index ? 
                                  <ChevronUp className="w-4 h-4" /> : 
                                  <ChevronDown className="w-4 h-4" />
                                }
                              </button>
                            </div>
                            
                            {/* Progress bar */}
                            <div className="flex items-center justify-between mb-2">
                              <div className={`w-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} rounded-full h-2 mr-2`}>
                                <div 
                                  className="h-2 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 transition-all duration-500" 
                                  style={{ width: `${book.progress}%` }}
                                />
                              </div>
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{book.progress}%</span>
                            </div>
                            
                            {/* Rating and action buttons */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-1">
                                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                <span className="text-sm">{book.rating}</span>
                                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>({book.reviews})</span>
                              </div>
                              <div className="flex space-x-2">
                                <button className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}>
                                  Read Now
                                </button>
                              </div>
                            </div>
                            
                            {/* Expanded content */}
                            {expandedBook === index && (
                              <div className="mt-4 pt-4 border-t border-gray-200/30">
                                <div className="flex justify-between mb-3">
                                  <div>
                                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      <span className="font-medium">Current Chapter:</span> {book.currentChapter}
                                    </p>
                                    {book.lastRead && (
                                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        <span className="font-medium">Last read:</span> {book.lastRead}
                                      </p>
                                    )}
                                  </div>
                                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {book.pages} pages • {book.format}
                                  </p>
                                </div>
                                
                                <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{book.summary}</p>
                                
                                {book.quotes.length > 0 && (
                                  <div className="mt-3">
                                    <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Your Highlights ({book.quotes.length})
                                    </div>
                                    <div className="space-y-2">
                                      {book.quotes.slice(0, 2).map((quote, qIndex) => (
                                        <div 
                                          key={qIndex} 
                                          className={`text-sm italic pl-3 border-l-2 ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-600'} py-1`}
                                        >
                                          "{quote}"
                                        </div>
                                      ))}
                                    </div>
                                    {book.quotes.length > 2 && (
                                      <button 
                                        className={`text-xs mt-2 text-blue-500 hover:text-blue-400`}
                                        onClick={() => setShowAllQuotes(!showAllQuotes)}
                                      >
                                        {showAllQuotes ? 'Show less' : `Show all ${book.quotes.length} highlights`}
                                      </button>
                                    )}
                                  </div>
                                )}
                                
                                <div className="mt-4 flex flex-wrap gap-2">
                                  <button className={`px-3 py-1 text-sm rounded-full ${darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'} transition-colors flex items-center`}>
                                    <Bookmark className="w-3 h-3 mr-1" /> Save
                                  </button>
                                  <button className={`px-3 py-1 text-sm rounded-full ${darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'} transition-colors flex items-center`}>
                                    <PenTool className="w-3 h-3 mr-1" /> Add Note
                                  </button>
                                  <button className={`px-3 py-1 text-sm rounded-full ${darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'} transition-colors flex items-center`}>
                                    <Share2 className="w-3 h-3 mr-1" /> Share
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Reading insights */}
                  <div className={`mt-6 p-5 rounded-2xl ${darkMode ? 'bg-gradient-to-r from-blue-900/30 to-indigo-900/30' : 'bg-gradient-to-r from-blue-100 to-indigo-100'} shadow-inner`}>
                    <div className="flex items-center space-x-3 mb-3">
                      <BarChart2 className="w-5 h-5 text-blue-400" />
                      <span className="font-medium">Your Reading Insights</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Books Completed</p>
                        <p className="text-xl font-medium">12</p>
                      </div>
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Avg. Rating</p>
                        <div className="flex items-center">
                          <p className="text-xl font-medium mr-2">4.2</p>
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        </div>
                      </div>
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pages Read</p>
                        <p className="text-xl font-medium">1,248</p>
                      </div>
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Highlights</p>
                        <p className="text-xl font-medium">87</p>
                      </div>
                    </div>
                    
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {learningStats.bestTime} • Based on your reading patterns
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* E-Library Tab */}
          {activeTab === 'library' && (
            <ELibrary />
          )}

          {/* Learning News Tab */}
          {activeTab === 'news' && (
            <LearningNews
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            />
          )} 

          {activeTab === 'discover' && (
            <DiscoveryLearning
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            />
          )}

          {activeTab === 'events' && (
            <EventLearning
            darkMode={darkMode}
            />
          )}

          {activeTab === 'settings' && (
            <SettingsHub
            darkMode={darkMode}
            />
          )}

          {activeTab === 'community' && (
            <CommunityLearning
            darkMode={darkMode}
            />
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <LearningAnalytics 
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LearningPlatformHome;